// Each inline "read more" rail used to slice the same `articles` array on its own, so an
// article that matched more than one rail's filter — or matched none, since the unfiltered
// "Dive deeper" rail took the top three regardless of what earlier rails had already shown —
// rendered in more than one rail on the same page. A country with a single Routes-tagged
// article (Germany) showed it in "Read more stories", "Dive deeper" and the footer grid: three
// times. `allocateArticleRails` walks the rails in page order and gives each one only the
// articles no earlier rail already claimed, so the same article never fills two rails
// (123yb691zu8).

export type ArticleRail<T> = {
	filter?: (article: T) => boolean;
	limit?: number;
};

export function allocateArticleRails<T>(articles: T[], rails: ArticleRail<T>[]): T[][] {
	const taken = new Set<T>();
	return rails.map(({ filter, limit = 3 }) => {
		const picked: T[] = [];
		for (const article of articles) {
			if (picked.length >= limit) break;
			if (taken.has(article)) continue;
			if (filter && !filter(article)) continue;
			picked.push(article);
			taken.add(article);
		}
		return picked;
	});
}

/**
 * The untranslated tag for an article card, for bucketing rails by category.
 *
 * `tag` became display text when the backend started resolving the rail against the localised
 * blog documents (`localizeArticleRail` in graphql/schemas/explore.ts), so on `/nl` it reads
 * "Reisroutes" and a `tag === "Routes"` test quietly matches nothing — the filtered rails empty
 * and everything falls through to the catch-all. `tagKey` is the English tag the guide document
 * carries; it is a lookup key and is never rendered or translated.
 *
 * Falls back to `tag` for English, where the two are the same string and the backend skips the
 * whole resolution step.
 */
export const tagKeyOf = (a: { tag?: string; tagKey?: string }): string | undefined => a.tagKey ?? a.tag;
